<template>
  <div class="full-container" v-if="comments.length || remainingReplies">
    <!-- List -->
    <div class="list-container" v-if="showReplies">
      <CommentSingle
        v-for="comment in comments"
        :key="comment.id"
        :nested="true"
        :post="post"
        :model="model"
        :data="comment"
        @delete="deleteCommentHandler"
        @reply="raiseReply"
      />
    </div>

    <!-- Error -->
    <div v-if="errorFetching">
      <p>
        {{ $t("error") }}
      </p>
      <button style="width: 100%" type="button" @click="loadComments">
        {{ $t("retry") }}
      </button>
    </div>
    <!-- Loading -->
    <div class="spinner" v-else-if="waitFetch">
      <Spinner />
    </div>

    <div 
      v-else-if="remainingReplies > 0"
      class="show-more-replies-container"
      @click="loadComments"
    >
      <div class="replies-decorator"></div>
      {{ $t("comments.placeholder.see_nth_responses", remainingReplies, { n: $n(remainingReplies) }) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { Post } from "~/types/Post";
import { Model } from "~/types/Model";
import { Comment } from "~/types/Comment";

import resources from "@/utils/resources";
import _NuxtApp from "../../types/NuxtApp";

const { $i18n: { t: $t, n: $n } } = <_NuxtApp>useNuxtApp();
const $emit = defineEmits(["delete", "reply", "changeHeight"]);

// Props
type Props = {
  post: Post;
  model: Model;
  commentId: number;
  replies: number;
};
const { post, model, commentId, replies }: Props = withDefaults(
  defineProps<Props>(),
  {
    post: {},
    model: {},
    commentId: 0,
    replies: 0,
  }
);

// Data
const comments = ref<Comment[]>([]);
const excludedComments = ref<Comment[]>([]);
const errorFetching = ref(false);
const waitFetch = ref(false);
const nextPage = ref(null);
const remainingReplies = ref(replies);
const showReplies = ref(true);

// Methods
const deleteCommentHandler = (commentId: number) => {
  const filterComment = (commentList: Comment[]) => {
    return commentList.filter((comment) => comment.id !== commentId);
  };
  
  excludedComments.value = filterComment(excludedComments.value);
  
  const oldLength = comments.value.length;
  comments.value = filterComment(comments.value);
  
  if (oldLength !== comments.value.length) $emit("delete");
};

const appendComment = (comment: Comment) => {
  excludedComments.value.push(comment.id);
  comments.value.push(comment);
  raiseChangeHeight();
};

const raiseReply = (reply: Comment) => {
  $emit("reply", reply);
};

const raiseChangeHeight = () => {
  nextTick(() => {
    $emit("changeHeight");
  });
};

const loadComments = () => {
  if (waitFetch.value) return;

  if (remainingReplies.value <= 0) {
    showReplies.value = false;
    remainingReplies.value = replies + excludedComments.value.length;
    return;
  }

  if (!showReplies.value) {
    showReplies.value = true;
    remainingReplies.value = 0;
    return;
  }

  waitFetch.value = true;
  errorFetching.value = false;

  let fetchRequest: Promise<{ 
    data: Comment[],
    meta: { 
      pagination: { 
        current_page: number,
        total_pages: number,
        links: { next: string },
        count: number
      },
    }
  }>;

  if (nextPage.value) {
    fetchRequest = resources.comments.getCommentsByPaginatorLink(
      nextPage.value
    );
  } else {
    fetchRequest = resources.comments.getReplies(
      post.post_id,
      commentId
    );
  }

  nextPage.value = null;

  fetchRequest
    .then((response) => {
      let data: Comment[] = response.data;
      const meta = response.meta;

      const oldLength = data.length;
      if (excludedComments.value.length) {
        data = data.filter(
          (comment) => !excludedComments.value.includes(comment.id)
        );
      }

      comments.value = comments.value.concat(data);
      raiseChangeHeight();

      if (!meta || !meta.pagination) return;
      const pagination = meta.pagination;

      if (
        pagination.current_page < meta.pagination.total_pages &&
        meta.pagination.links
      ) {
        nextPage.value = meta.pagination.links.next;
      }

      remainingReplies.value -= meta.pagination.count - (oldLength - data.length);
    })
    .catch((error) => {
      console.log(error);
      errorFetching.value = true;
    })
    .finally(() => {
      waitFetch.value = false;
    });
}

defineExpose({
  appendComment,
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables.scss";

.show-more-replies-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  font-size: 0.9em;
  font-weight: 500;

  color: var(--pallete_dark_text_color);

  -webkit-tap-highlight-color: transparent;
  background: none;

  &:hover {
    cursor: pointer;
  }

  > .replies-decorator {
    height: 1px;
    width: 2.5em;
    border-top: 1px solid var(--pallete_dark_text_color);
    margin-block: auto;
    margin-right: 0.5em;
  }
}

.full-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}

.list-container {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  margin: 0;
  padding: 0;
}

.spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}
</style>
